import { useCallback, useEffect, useState } from 'react';
import type { Json } from '@/lib/database.types';
import { GRID_SETTING_KEYS, loadGridSettings, saveGridSetting } from '@/shared/components/Grid';
import { Icon } from '@/shared/components/ui/Icon';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';
import {
  applyThemeLayout,
  sanitizeThemeLayout,
  THEME_LAYOUT_LIMITS,
  type ThemeLayout,
  type ThemeLayoutSize,
} from '@/shared/theme';

/** The sizes, in the order shown; each one's colour is a theme role above. */
const SIZES: readonly { key: ThemeLayoutSize; labelKey: MessageKey }[] = [
  { key: 'cellFontSize', labelKey: 'settings.themeLayout.cellFontSize' },
  { key: 'rowHeight', labelKey: 'settings.themeLayout.rowHeight' },
  { key: 'headerFontSize', labelKey: 'settings.themeLayout.headerFontSize' },
  { key: 'sheetNameFontSize', labelKey: 'settings.themeLayout.sheetNameFontSize' },
  { key: 'groupFontSize', labelKey: 'settings.themeLayout.groupFontSize' },
  { key: 'groupRowHeight', labelKey: 'settings.themeLayout.groupRowHeight' },
];

/**
 * Sizes and grid lines for every spreadsheet of the organization. Autosaved and
 * applied at once, like the colours: the real grid is the preview.
 */
export function ThemeLayoutPanel({ orgId, editable }: { orgId: string; editable: boolean }) {
  const { t } = useI18n();
  const [layout, setLayout] = useState<ThemeLayout>({});
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadGridSettings(orgId)
      .then((settings) => { if (active) setLayout(sanitizeThemeLayout(settings[GRID_SETTING_KEYS.layout])); })
      .catch(() => { if (active) setError(t('settings.themeColors.loadError')); });
    return () => { active = false; };
  }, [orgId, t]);

  const commit = useCallback((next: ThemeLayout) => {
    setLayout(next);
    applyThemeLayout(next);
    setError(null);
    void saveGridSetting(orgId, GRID_SETTING_KEYS.layout, next as unknown as Json)
      .then((failure) => { if (failure) setError(t('settings.themeColors.saveError')); });
  }, [orgId, t]);

  const set = (patch: Partial<Record<keyof ThemeLayout, boolean | number | undefined>>) => {
    const next: Record<string, unknown> = { ...layout, ...patch };
    for (const key of Object.keys(next)) if (next[key] === undefined) delete next[key];
    commit(sanitizeThemeLayout(next));
  };

  const toggle = (key: 'altRows' | 'verticalLines', label: MessageKey, fallback: boolean) => {
    const checked = layout[key] ?? fallback;
    return (
      <label className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border bg-surface-2 px-3 py-2">
        <span className="min-w-0 flex-1 break-words text-sm text-fg">{t(label)}</span>
        <input
          type="checkbox"
          checked={checked}
          disabled={!editable}
          onChange={(e) => set({ [key]: e.target.checked === fallback ? undefined : e.target.checked })}
          className="h-5 w-5 shrink-0 accent-brand"
        />
      </label>
    );
  };

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="tap-target flex w-full min-w-0 items-center justify-between gap-2 px-4 text-left"
      >
        <span className="min-w-0 truncate text-sm font-bold text-fg">{t('settings.themeLayout.title')}</span>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} className="h-4 w-4 shrink-0 text-muted" />
      </button>
      {open && (
        <div className="flex flex-col gap-2 border-t border-border p-3">
          <p className="text-xs text-muted">{t('settings.themeLayout.description')}</p>
          {error && <p className="text-xs text-danger">{error}</p>}
          {toggle('altRows', 'settings.themeLayout.altRows', true)}
          {toggle('verticalLines', 'settings.themeLayout.verticalLines', false)}
          {SIZES.map(({ key, labelKey }) => {
            const { min, max, placeholder } = THEME_LAYOUT_LIMITS[key];
            return (
              <label key={key} className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border bg-surface-2 px-3 py-2">
                <span className="min-w-0 flex-1 break-words text-sm text-fg">{t(labelKey)}</span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted">
                  <input
                    // Remounts when the stored value changes, so a clamped entry shows its real value.
                    key={layout[key] ?? 'default'}
                    type="number"
                    inputMode="numeric"
                    min={min}
                    max={max}
                    defaultValue={layout[key] ?? ''}
                    placeholder={String(placeholder)}
                    disabled={!editable}
                    onBlur={(e) => {
                      const text = e.target.value.trim();
                      const value = text === '' ? undefined : Number(text);
                      if (value !== layout[key]) set({ [key]: value });
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    className="nt-control w-20 text-right"
                    aria-label={t(labelKey)}
                  />
                  px
                </span>
              </label>
            );
          })}
          <div>
            <button
              type="button"
              disabled={!editable || Object.keys(layout).length === 0}
              onClick={() => commit({})}
              className="tap-target rounded-xl border border-border bg-surface px-4 text-sm font-bold text-fg transition-colors hover:bg-surface-3 disabled:opacity-60"
            >
              {t('settings.themeLayout.reset')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
